import { push } from "connected-react-router";
import isNil from "lodash/isNil";
import { fork, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { handleApiErrors } from "api/helpers/handleApiErrors";
import { toastForApiErrors } from "api/helpers/toastForApiErrors";
import { portalApi, TAGS } from "api/rtkApi";
import { showMessageToast } from "components/toasts/MessageToast";
import i18n from "i18next";
import { selectCurrentCustomerId } from "modules/auth";
import { callApi } from "sagas/helpers/api";
import {
  selectEnvironment,
  selectGlobalFilteredCustomer,
  setError,
  setGlobalFilteredCustomer,
  setSuccess,
  startLoading,
} from "store/appSlice";
import { DATE_FORMAT_DD_MM_YYYY, transformFormat, UUID } from "utils";

import * as CustomersApi from "../api/Customers";
import { Customer, INCORPORATION_DATE_FORMAT } from "../models/Customer";
import {
  addItem,
  createCustomer,
  customerOperationPaths,
  customersOperationPaths,
  fetchCustomer,
  fetchCustomerBanksByCountry,
  fetchCustomers,
  setCustomerBanksByCountry,
  setItem,
  setList,
  updateCustomer,
  updateCustomerBanksByCountry,
} from "./customers.slice";

import type { PayloadAction } from "@reduxjs/toolkit";
import type { APPLICATION_ENV } from "constants/environment";
import type { CountryCode } from "models/CountryCode";
import type GlobalFilteredCustomer from "models/GlobalFilteredCustomer";
import type { ObjectWithId } from "utils/ObjectWithId";
import type { CustomerFormValues } from "../components/customer-form-content/forms/CustomerForm";

const credentialMap = {
  production: "prod",
  sandbox: "sandbox",
};

function transformToCustomer(
  {
    credentials,
    children,
    documentNumber,
    ...customer
  }: CustomersApi.GetCustomerResponse,
  environment: APPLICATION_ENV
): Customer {
  if (!credentials) {
    return {
      ...customer,
      documentNumber: documentNumber.toString(),
      descendantsCount: children.length,
    };
  }

  let updatedCredential: Customer["credentials"];
  const envCredentialIdx = credentials.findIndex(
    (cred) => credentialMap[cred.environment] === environment
  );

  if (envCredentialIdx > -1) {
    const { createdAt, ...credential } = credentials[envCredentialIdx];

    updatedCredential = {
      ...credential,
      password: "",
      username: credential.email,
    };
  }

  return {
    ...customer,
    documentNumber: documentNumber.toString(),
    children: children.map((child) => transformToCustomer(child, environment)),
    descendantsCount: children.length,
    credentials: updatedCredential,
  };
}

function* fetchCustomersSaga({
  payload,
}: PayloadAction<{ customerId: string; search?: string }>) {
  const { customerId, search } = payload ?? {};
  const { listing: operationPath } = customerOperationPaths();
  const currentEnvironment: APPLICATION_ENV = yield select(selectEnvironment);

  yield put(startLoading(operationPath));
  try {
    const data: {
      list: CustomersApi.GetCustomerResponse[];
    } = yield callApi(CustomersApi.getCustomers, { customerId, search });

    yield put(
      setList({
        list: data.list.map((customer) => {
          return transformToCustomer(customer, currentEnvironment);
        }),
      })
    );
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* fetchCustomerSaga({ payload }: PayloadAction<ObjectWithId>) {
  const { id } = payload ?? {};
  const currentEnvironment: APPLICATION_ENV = yield select(selectEnvironment);
  const { details: operationPath } = customerOperationPaths(id);

  yield put(startLoading(operationPath));

  try {
    const {
      incorporationDate,
      ...item
    }: CustomersApi.GetCustomerResponse = yield callApi(
      CustomersApi.getCustomer,
      id
    );

    yield put(
      setItem({
        id,
        item: transformToCustomer(
          {
            ...item,
            incorporationDate:
              incorporationDate !== ""
                ? transformFormat(incorporationDate, INCORPORATION_DATE_FORMAT)
                : "",
          },
          currentEnvironment
        ),
      })
    );

    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* fetchCustomerBanksByCountrySaga({
  payload,
}: PayloadAction<ObjectWithId>) {
  const { id } = payload ?? {};

  yield put(startLoading(customersOperationPaths.banksByCountry));

  try {
    const data: CountryCode[] = yield callApi(
      CustomersApi.getCustomerBanksByCountry,
      id
    );

    yield put(setCustomerBanksByCountry(data));
    yield put(setSuccess(customersOperationPaths.banksByCountry));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: customersOperationPaths.banksByCountry,
        error,
      })
    );
  }
}

function* createCustomerSaga({ payload }: PayloadAction<CustomerFormValues>) {
  const data = {
    ...payload,
    incorporationDate:
      payload.incorporationDate &&
      transformFormat(
        payload.incorporationDate,
        DATE_FORMAT_DD_MM_YYYY,
        INCORPORATION_DATE_FORMAT
      ),
  };

  const { create: operationPath } = customerOperationPaths();

  yield put(startLoading(operationPath));
  const customerId: UUID = yield select(selectCurrentCustomerId);
  const currentEnvironment: APPLICATION_ENV = yield select(selectEnvironment);

  try {
    const globalFilteredCustomer: GlobalFilteredCustomer | null = yield select(
      selectGlobalFilteredCustomer
    );

    const customer: CustomersApi.GetCustomerResponse = yield callApi(
      CustomersApi.postCustomer,
      {
        ...data,
        customer: customerId,
      }
    );

    yield put(addItem(transformToCustomer(customer, currentEnvironment)));
    yield put(setSuccess(operationPath));
    yield put(push(`/configuration/customers/${customer.id}`));
    yield put(portalApi.util.invalidateTags([TAGS.CUSTOMERS]));

    if (
      globalFilteredCustomer &&
      Array.isArray(globalFilteredCustomer.allIds) &&
      !isNil(data.parent)
    ) {
      if (globalFilteredCustomer.allIds.indexOf(data.parent) >= 0) {
        yield put(
          setGlobalFilteredCustomer({
            ...globalFilteredCustomer,
            allIds: [...globalFilteredCustomer.allIds, customer.id],
          })
        );
      }
    }

    const parentAccountsLength = data.parentAccounts?.length;

    if (parentAccountsLength) {
      const toastMessage =
        parentAccountsLength === 1
          ? "Selected bank account was activated for the merchant"
          : "Selected bank accounts were activated for the merchant";

      showMessageToast({
        bodyContent: toastMessage,
        variant: "success",
      });
    }
  } catch (error) {
    const errors = handleApiErrors(error);

    if (errors) {
      toastForApiErrors(errors);
    }

    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* updateCustomerSaga({
  payload,
}: PayloadAction<{
  id: UUID;
  data: CustomerFormValues;
  accountsToOverriden?: number;
}>) {
  const { id, data, accountsToOverriden } = payload ?? {};

  const { update: operationPath } = customerOperationPaths(id);

  yield put(startLoading(operationPath));
  const currentEnvironment: APPLICATION_ENV = yield select(selectEnvironment);

  data.incorporationDate = transformFormat(
    data.incorporationDate as string,
    DATE_FORMAT_DD_MM_YYYY,
    INCORPORATION_DATE_FORMAT
  );

  try {
    const customer: CustomersApi.GetCustomerResponse = yield callApi(
      CustomersApi.patchCustomer,
      id,
      {
        ...data,
      }
    );

    setItem({
      id,
      item: transformToCustomer(customer, currentEnvironment),
    });

    yield put(setSuccess(operationPath));
    yield put(portalApi.util.invalidateTags([TAGS.CUSTOMERS]));

    if (accountsToOverriden) {
      showMessageToast({
        bodyContent: i18n.t("customers:overridenAccounts", {
          count: accountsToOverriden,
        }) as string,
        variant: "success",
      });
    }

    yield put(push(`/configuration/customers/${customer.id}`));
  } catch (error) {
    const errors = handleApiErrors(error);

    if (errors) {
      toastForApiErrors(errors);
    }

    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* updateCustomerBanksByCountrySaga({
  payload,
}: PayloadAction<{
  id: UUID;
  countries: string[];
}>) {
  const { id, countries } = payload ?? {};

  yield put(startLoading(customersOperationPaths.banksUpdate));

  try {
    yield callApi(CustomersApi.patchCustomerBanksByCountry, id, countries);
    yield put(setSuccess(customersOperationPaths.banksUpdate));
  } catch (error) {
    yield put(
      setError({
        path: customersOperationPaths.banksUpdate,
        error,
      })
    );
  }
}

function* watchFetchCustomers() {
  yield takeLatest(fetchCustomers.type, fetchCustomersSaga);
}

function* watchFetchCustomer() {
  yield takeLatest(fetchCustomer.type, fetchCustomerSaga);
}

function* watchFetchCustomerBanksByCountry() {
  yield takeLatest(
    fetchCustomerBanksByCountry.type,
    fetchCustomerBanksByCountrySaga
  );
}

function* watchUpdateCustomerBanksByCountry() {
  yield takeLatest(
    updateCustomerBanksByCountry.type,
    updateCustomerBanksByCountrySaga
  );
}

function* watchCreateCustomer() {
  yield takeEvery(createCustomer.type, createCustomerSaga);
}

function* watchUpdateCustomer() {
  yield takeEvery(updateCustomer.type, updateCustomerSaga);
}

export default function* rootSaga() {
  yield fork(watchFetchCustomers);
  yield fork(watchFetchCustomer);
  yield fork(watchFetchCustomerBanksByCountry);
  yield fork(watchUpdateCustomerBanksByCountry);
  yield fork(watchCreateCustomer);
  yield fork(watchUpdateCustomer);
}
